import React, { useState, useRef, useEffect } from 'react';
import api from '../api';

const ElitureChat = ({ user }) => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    { role: 'ai', text: 'Hi! I\'m your Eliture learning assistant. Ask me anything about your courses, certifications, or learning goals! 🚀' }
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  useEffect(() => { if (open) scrollToBottom(); }, [messages, open]);

  const sendMessage = async () => {
    const text = input.trim();
    if (!text || loading) return;
    setInput('');
    setMessages(prev => [...prev, { role: 'user', text }]);
    setLoading(true);
    try {
      const res = await api.post('/ai/chat', {
        message: text,
        userId: user?.id || 'demo-user-id',
        context: { userId: user?.id, progress: {} },
      });
      setMessages(prev => [...prev, { role: 'ai', text: res.data.reply || 'Got it! Let me help you with that.' }]);
    } catch {
      setMessages(prev => [...prev, {
        role: 'ai',
        text: '💡 I can help you navigate your courses, set learning goals, or understand complex topics. What would you like to explore today?',
      }]);
    }
    setLoading(false);
  };

  const quickPrompts = [
    'What should I study next?',
    'Explain Generative AI in simple terms',
    'How do I prepare for GenAI certification?',
  ];

  return (
    <>
      {/* Floating Button */}
      {!open && (
        <button onClick={() => setOpen(true)} style={{
          position: 'fixed', bottom: 28, right: 28,
          width: 58, height: 58,
          background: 'linear-gradient(135deg, #6366f1, #10b981)',
          border: 'none', borderRadius: '50%',
          cursor: 'pointer', zIndex: 500,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 24,
          boxShadow: '0 8px 24px rgba(99,102,241,0.4), 0 0 0 0 rgba(99,102,241,0.4)',
          animation: 'pulse 2s infinite',
          transition: 'transform 0.2s',
        }}
          onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.1)'}
          onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'}
          title="Chat with Eliture AI"
        >
          💬
        </button>
      )}

      {/* Chat Widget */}
      {open && (
        <div style={{
          position: 'fixed', bottom: 28, right: 28,
          width: 360, height: 520,
          background: 'rgba(15, 12, 41, 0.95)',
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(99,102,241,0.3)',
          borderRadius: 20, zIndex: 500,
          display: 'flex', flexDirection: 'column',
          boxShadow: '0 24px 64px rgba(0,0,0,0.5)',
          animation: 'slideUp 0.3s cubic-bezier(.16,1,.3,1)',
          overflow: 'hidden',
        }}>
          <style>{`
            @keyframes slideUp { from { opacity: 0; transform: translateY(20px) scale(0.95); } to { opacity: 1; transform: translateY(0) scale(1); } }
            @keyframes pulse { 0%,100% { box-shadow: 0 8px 24px rgba(99,102,241,0.4), 0 0 0 0 rgba(99,102,241,0.4); } 50% { box-shadow: 0 8px 24px rgba(99,102,241,0.4), 0 0 0 10px rgba(99,102,241,0); } }
            .chat-input:focus { border-color: rgba(99,102,241,0.5) !important; outline: none; }
            .chat-msg-ai { animation: msgIn 0.2s ease; }
            @keyframes msgIn { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }
            ::-webkit-scrollbar { width: 4px; }
            ::-webkit-scrollbar-track { background: transparent; }
            ::-webkit-scrollbar-thumb { background: rgba(99,102,241,0.3); border-radius: 2px; }
          `}</style>

          {/* Header */}
          <div style={{ background: 'linear-gradient(135deg, rgba(99,102,241,0.3), rgba(16,185,129,0.2))',
            padding: '16px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            borderBottom: '1px solid rgba(99,102,241,0.2)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{ width: 36, height: 36, background: 'linear-gradient(135deg, #6366f1, #10b981)',
                borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16 }}>
                🎓
              </div>
              <div>
                <div style={{ fontWeight: 700, color: '#fff', fontSize: '0.9rem' }}>Eliture AI</div>
                <div style={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.5)', display: 'flex', alignItems: 'center', gap: 4 }}>
                  <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#10b981', display: 'inline-block' }} />
                  Learning Assistant
                </div>
              </div>
            </div>
            <button onClick={() => setOpen(false)} style={{
              background: 'rgba(255,255,255,0.08)', border: 'none',
              borderRadius: '50%', width: 30, height: 30,
              color: 'rgba(255,255,255,0.6)', cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14,
            }}>✕</button>
          </div>

          {/* Messages */}
          <div style={{ flex: 1, overflowY: 'auto', padding: '16px 16px 8px' }}>
            {messages.map((msg, i) => (
              <div key={i} className={msg.role === 'ai' ? 'chat-msg-ai' : ''} style={{
                display: 'flex', marginBottom: 12,
                justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start',
                gap: 8, alignItems: 'flex-end',
              }}>
                {msg.role === 'ai' && (
                  <div style={{ width: 28, height: 28, flexShrink: 0, borderRadius: '50%',
                    background: 'linear-gradient(135deg, #6366f1, #10b981)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12 }}>🎓</div>
                )}
                <div style={{
                  maxWidth: '80%', padding: '10px 14px', borderRadius: msg.role === 'user' ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
                  background: msg.role === 'user'
                    ? 'linear-gradient(135deg, rgba(99,102,241,0.4), rgba(99,102,241,0.3))'
                    : 'rgba(255,255,255,0.07)',
                  border: msg.role === 'user' ? 'none' : '1px solid rgba(255,255,255,0.06)',
                  color: '#fff', fontSize: '0.85rem', lineHeight: 1.5,
                }}>
                  {typeof msg.text === 'object' ? JSON.stringify(msg.text) : msg.text}
                </div>
              </div>
            ))}
            {loading && (
              <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', marginBottom: 12 }}>
                <div style={{ width: 28, height: 28, borderRadius: '50%',
                  background: 'linear-gradient(135deg, #6366f1, #10b981)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12 }}>🎓</div>
                <div style={{ background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.06)',
                  borderRadius: '16px 16px 16px 4px', padding: '12px 16px', display: 'flex', gap: 4, alignItems: 'center' }}>
                  {[0, 1, 2].map(d => (
                    <div key={d} style={{ width: 6, height: 6, borderRadius: '50%', background: '#6366f1',
                      animation: `bounce 1s ${d * 0.2}s infinite` }} />
                  ))}
                  <style>{`@keyframes bounce { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-5px)} }`}</style>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Quick Prompts */}
          {messages.length <= 1 && (
            <div style={{ padding: '0 12px 8px', display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {quickPrompts.map((p, i) => (
                <button key={i} onClick={() => { setInput(p); }} style={{
                  background: 'rgba(99,102,241,0.1)', border: '1px solid rgba(99,102,241,0.2)',
                  borderRadius: 20, padding: '5px 12px', color: 'rgba(255,255,255,0.7)',
                  fontSize: '0.72rem', cursor: 'pointer',
                }}>{p}</button>
              ))}
            </div>
          )}

          {/* Input */}
          <div style={{ padding: '12px 14px', borderTop: '1px solid rgba(255,255,255,0.06)',
            display: 'flex', gap: 8 }}>
            <input className="chat-input" value={input} onChange={e => setInput(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && sendMessage()}
              placeholder="Ask anything about learning..."
              style={{ flex: 1, background: 'rgba(255,255,255,0.06)',
                border: '1px solid rgba(99,102,241,0.2)', borderRadius: 12,
                padding: '10px 14px', color: '#fff', fontSize: '0.85rem', fontFamily: 'inherit' }} />
            <button onClick={sendMessage} disabled={!input.trim() || loading} style={{
              width: 40, height: 40, borderRadius: 12, border: 'none',
              background: input.trim() ? 'linear-gradient(135deg, #6366f1, #10b981)' : 'rgba(255,255,255,0.05)',
              cursor: input.trim() ? 'pointer' : 'not-allowed',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 16, flexShrink: 0, transition: 'all 0.2s',
            }}>→</button>
          </div>
        </div>
      )}
    </>
  );
};

export default ElitureChat;
